import Daily, {
  DailyEventObjectInputSettingsUpdated,
  DailyInputAudioProcessorSettings,
  DailyInputSettings,
} from '@daily-co/daily-js';
import { useInputSettings } from '@daily-co/daily-react';
import deepEqual from 'fast-deep-equal';
import Cookies from 'js-cookie';
import { useCallback, useRef } from 'react';
import { atom, useRecoilCallback, useRecoilValue } from 'recoil';

import { useCallConfig } from './useCallConfig';

type AudioProcessor = DailyInputAudioProcessorSettings['type'];
const audioProcessors: AudioProcessor[] = ['none', 'noise-cancellation'];
const COOKIE_KEY = 'audio-processor';

const selectedAudioProcessorState = atom<AudioProcessor>({
  key: 'selected-audio-processor',
  default: 'none',
  effects: [
    /**
     * Synchronizes atom state with cookie.
     */
    ({ onSet, setSelf, trigger }) => {
      // not all browsers support audio processing, e.g. Safari. in that case, there's
      // no reason to look at cookies.
      if (!Daily.supportedBrowser().supportsAudioProcessing) return;
      if (trigger === 'get') {
        const storedAudioProcessor = Cookies.get(COOKIE_KEY) as AudioProcessor;
        if (audioProcessors.includes(storedAudioProcessor)) {
          setSelf(storedAudioProcessor);
        }
      }
      onSet((newAudioProcessor) => {
        if (newAudioProcessor === 'none') {
          Cookies.remove(COOKIE_KEY, {
            sameSite: 'none',
            secure: true,
          });
        } else {
          Cookies.set(COOKIE_KEY, newAudioProcessor, {
            expires: new Date('Tue, 19 Jan 2038 03:14:07 GMT'),
            sameSite: 'none',
            secure: true,
          });
        }
      });
    },
  ],
});

const noiseCancellationDisabledState = atom<boolean>({
  key: 'noise-cancellation-disabled',
  default: false,
});

export const useAudioProcessor = () => {
  const { enableNoiseCancellationUI } = useCallConfig();
  const waitForUpdate = useRef<boolean>(false);
  const noiseCancellationDisabled = useRecoilValue(
    noiseCancellationDisabledState
  );
  const currentAudioProcessor = useRecoilValue(selectedAudioProcessorState);

  /* Update the atom state when the input settings change */
  const { inputSettings, updateInputSettings } = useInputSettings({
    onInputSettingsUpdated: useRecoilCallback(
      ({ set }) =>
        (event: DailyEventObjectInputSettingsUpdated) => {
          if (!event.inputSettings?.audio) return;

          const type = event.inputSettings?.audio?.processor?.type;

          switch (type) {
            case 'noise-cancellation':
              set(selectedAudioProcessorState, 'noise-cancellation');
              break;
            case 'none':
              set(selectedAudioProcessorState, 'none');
              break;
          }

          if (waitForUpdate.current) {
            waitForUpdate.current = false;
            set(noiseCancellationDisabledState, false);
          }
        },
      []
    ),
  });

  const audioProcessorType = inputSettings?.audio?.processor?.type;

  /* Update the input settings */
  const setAudioProcessor = useCallback(
    (audioProcessor: AudioProcessor) => {
      if (!enableNoiseCancellationUI) return;

      try {
        const settings: DailyInputSettings = {
          audio: {
            processor: {
              type: audioProcessor,
            },
          },
        };

        // old and new settings the same, so return
        if (deepEqual(inputSettings?.audio, settings?.audio)) {
          return;
        }

        //  the current input settings is an empty object, and the new input settings
        //  do not actually change anything, so no-op
        if (deepEqual(inputSettings, {}) && audioProcessor === 'none') {
          return;
        }

        updateInputSettings(settings);
      } catch {}
    },
    [enableNoiseCancellationUI, inputSettings, updateInputSettings]
  );

  const handleNoiseCancellationChange = useRecoilCallback(
    ({ set }) =>
      (isOn: boolean) => {
        set(noiseCancellationDisabledState, true);
        waitForUpdate.current = true;
        setAudioProcessor(isOn ? 'noise-cancellation' : 'none');
      },
    [setAudioProcessor]
  );

  return {
    noiseCancellationOn: audioProcessorType === 'noise-cancellation',
    noiseCancellationDisabled,
    handleNoiseCancellationChange,
    currentAudioProcessor,
    setAudioProcessor,
  };
};
